import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../../axios/api.js';

// Thunks backing the `FollowUpBiddingBudgetary` route (FollowUpBiddingBuying).
// Only what differs from the buying route lives here — the three `-bidding`
// list endpoints, the `-bidding` country filter and the move-to-buying action.
// The lead-detail lookup, solitaire-company check, move-to-cold-calling,
// scheduled-date update and review-remark reads are shared and imported from
// `followUpSlice` by the page.
//
// As on the buying route, list thunks return rows untransformed: the page maps
// them through its component-scoped IST helpers before storing.

// Always returns a string. A 500 whose body is `{"error":{"code":..,"detail":..}}`
// would otherwise hand an object to `state.error`, and rendering that object
// takes out the whole page via the error boundary. Picks the first candidate
// that is actually a non-empty string, so an object-shaped `data.error` falls
// through to `data.message` instead of short-circuiting onto the fallback.
const asMessage = (error, fallback) =>
  [error.response?.data?.error, error.response?.data?.message, error.message]
    .find((value) => typeof value === 'string' && value.trim()) || fallback;

// Rejection payload for the three list thunks, which are the only ones whose
// rejection reaches the UI. `message` is safe to render but is NOT rendered:
// the page shows `status` alone, because the upstream string can carry SQL
// fragments and host names. `detail` exists purely for the caller's
// `console.error` and must never be rendered.
const asListError = (error, fallback) => ({
  status: error.response?.status ?? null,
  message: asMessage(error, fallback),
  detail: error.response?.data ?? error.message,
});

export const fetchBiddingDue = createAsyncThunk(
  'followUpBidding/fetchBiddingDue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduled-bidding');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const fetchBiddingUpcoming = createAsyncThunk(
  'followUpBidding/fetchBiddingUpcoming',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduledupcoming-bidding');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const fetchBiddingPrevious = createAsyncThunk(
  'followUpBidding/fetchBiddingPrevious',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduledprevious-bidding');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const applyBiddingCountryFilter = createAsyncThunk(
  'followUpBidding/applyBiddingCountryFilter',
  async ({ selectedLeads, updateComingState, previousState }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/followupCountryFilter-bidding', {
        selectedLeads,
        updateComingState,
        previousState,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to apply country filter'));
    }
  }
);

export const moveToBuying = createAsyncThunk(
  'followUpBidding/moveToBuying',
  async (rfqNo, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/move-to-buying/${rfqNo}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move lead to buying'));
    }
  }
);

const initialState = {
  leads: [],
  filteredLeads: [],
  loading: true,
  error: null,
  errorStatus: null,
};

const followUpBiddingSlice = createSlice({
  name: 'followUpBidding',
  initialState,
  reducers: {
    setBiddingLeads: (state, action) => {
      state.leads = action.payload;
    },
    setBiddingFilteredLeads: (state, action) => {
      state.filteredLeads = action.payload;
    },
    setBiddingLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearBiddingError: (state) => {
      state.error = null;
      state.errorStatus = null;
    },
    resetBiddingState: () => initialState,
  },
  extraReducers: (builder) => {
    const listPending = (state) => {
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
    };
    const listRejected = (state, action) => {
      state.loading = false;
      // `asListError` guarantees a string here; the `?? ` is belt-and-braces
      // against a rejection that bypassed it (e.g. a thrown non-Error).
      state.error = typeof action.payload?.message === 'string'
        ? action.payload.message
        : 'Error fetching data';
      state.errorStatus = action.payload?.status ?? null;
    };
    builder
      .addCase(fetchBiddingDue.pending, listPending)
      .addCase(fetchBiddingDue.rejected, listRejected)
      .addCase(fetchBiddingUpcoming.pending, listPending)
      .addCase(fetchBiddingUpcoming.rejected, listRejected)
      .addCase(fetchBiddingPrevious.pending, listPending)
      .addCase(fetchBiddingPrevious.rejected, listRejected);
  },
});

export const {
  setBiddingLeads,
  setBiddingFilteredLeads,
  setBiddingLoading,
  clearBiddingError,
  resetBiddingState,
} = followUpBiddingSlice.actions;

export default followUpBiddingSlice.reducer;
